import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useApiFetch } from '../../shared/api'

export interface ReportSharingInfo {
  /** Whether the ended hunt's report is currently visible to its participants. */
  isShared: boolean
  participantCount: number
  participantNames: string[]
  /**
   * Whether this is an estate hunt. Sharing an ended report with the participants
   * is an estate-team feature, so the card is offered only when this is true — the
   * client gets the answer, never the team's raw marker.
   */
  isEstate: boolean
}

/** The single field the toggle sets. */
export interface SetReportSharingRequest {
  shared: boolean
}

/** The server echoes the stored value back, so the client never guesses it. */
export interface SetReportSharingResult {
  shared: boolean
}

function base(groupId: string | undefined, huntId: string | undefined): string {
  return `/clubhouse/groups/${encodeURIComponent(groupId ?? '')}/hunts/${encodeURIComponent(huntId ?? '')}/report-sharing`
}

/**
 * Whether an ended hunt's report is shared with its participants, who would see it
 * and how many. The server enforces owner/admin/leader and returns 403 to anyone
 * else, so a plain participant's query fails and the card is never shown.
 */
export function useReportSharingInfo(
  groupId: string | undefined, huntId: string | undefined, enabled = true,
) {
  const call = useApiFetch()
  return useQuery({
    queryKey: ['clubhouse', 'hunts', groupId, huntId, 'report-sharing'],
    queryFn: () => call<ReportSharingInfo>(base(groupId, huntId)),
    enabled: enabled && !!groupId && !!huntId,
    // The toggle reflects the server's state, so the card must open on a fresh
    // read rather than a cached one — otherwise it could show "shared" the server
    // never granted. Revalidate on every open.
    staleTime: 0,
  })
}

/**
 * Set whether the report is shared. Returns the server's confirmed value; a 403
 * `report_sharing.estate_only` and a 409 `report_sharing.hunt_not_ended` surface as
 * ApiErrors the caller branches on. On success the info query is invalidated so the
 * card re-renders from the confirmed state.
 */
export function useSetReportSharing(groupId: string | undefined, huntId: string | undefined) {
  const call = useApiFetch()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (request: SetReportSharingRequest) =>
      call<SetReportSharingResult>(base(groupId, huntId), { method: 'PUT', body: JSON.stringify(request) }),
    onSuccess: () => {
      // Fire-and-forget: the refetch is best-effort, so the promise is
      // intentionally not awaited (and its rejection is not left floating).
      void qc.invalidateQueries({ queryKey: ['clubhouse', 'hunts', groupId, huntId, 'report-sharing'] })
    },
    onError: (e) => {
      console.error('Failed to set report sharing', e)
    },
  })
}
